import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ClipboardList, Download, Flag, PanelRightClose, Plus } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { Badge } from '../ui/Badge';
import { cn } from '../../lib/utils';
import { formatSlashDate } from '../../utils/dateUtils';
import { isSmartLightEmployee } from '../../authUtils';
import { fetchEngagementReferrals } from '../../services/carrierEngagementService';
import { useCarrierEngagementApiFilters } from '../../contexts/CarrierEngagementFilterContext';
import { EngagementReferralDrawer } from './EngagementReferralDrawer';
import { MedicalRecordsDrawer } from './MedicalRecordsDrawer';
import { Tooltip } from '../ui/Tooltip';
import { SlDispositionModal } from '../referral-drawer/SlDispositionModal';

// Cells that open the SL-disposition modal when the viewer is an SL employee.
const SL_EDITABLE_COLUMNS = new Set(['sl_response', 'sl_reason', 'sl_action', 'latest_comment']);

// Column shape mirrors CP's REFERRAL_COLUMNS for visual parity:
//   { key, label, displayKey?, render?, truncate?, textSize?, className? }
// `displayKey` is the field name on the API row; falls back to `key`.
const OVERVIEW_COLUMNS = [
  { key: 'client', label: 'Client', textSize: 'text-sm', className: 'whitespace-nowrap' },
  { key: 'tpa_carrier', label: 'TPA/Carrier', textSize: 'text-sm', className: 'whitespace-nowrap' },
  {
    key: 'referral_type',
    label: 'Referral Type',
    render: (val) =>
      val ? (
        <Badge variant="outline" className="rounded px-2 py-0 font-medium whitespace-nowrap">
          {val}
        </Badge>
      ) : '',
  },
  { key: 'bucket', label: 'Bucket', className: 'whitespace-nowrap' },
  { key: 'referral_scheme', label: 'Referral Scheme', className: 'whitespace-nowrap' },
  {
    key: 'publish_dt',
    label: 'Publish Date',
    className: 'text-muted-foreground whitespace-nowrap',
    render: (val) => (val ? formatSlashDate(val) : ''),
  },
  {
    key: 'aging_out',
    label: 'Aging Out',
    render: (val) => (val ? <Flag className="h-3.5 w-3.5 text-destructive mx-auto" /> : ''),
  },
  { key: 'referral_id', label: 'Referral ID', textSize: 'text-sm', className: 'font-medium whitespace-nowrap' },
  { key: 'c_score', label: 'C Score', className: 'tabular-nums text-center whitespace-nowrap' },
  {
    key: 'referral_name',
    label: 'Referral Name',
    truncate: true,
    className: 'max-w-[180px]',
  },
  { key: 'identified', label: 'Identified', className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'realized', label: 'Realized', className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'leakage', label: 'Leakage', className: 'tabular-nums text-right whitespace-nowrap' },
  { key: 'base_status', label: 'Base Status', className: 'whitespace-nowrap' },
  { key: 'tpa_carrier_status', label: 'TPA/Carrier Status', className: 'whitespace-nowrap' },
  {
    key: 'tpa_carrier_reason',
    label: 'TPA/Carrier Reason',
    truncate: true,
    className: 'max-w-[130px] text-muted-foreground',
  },
  { key: 'sl_response', label: 'SL Response', className: 'whitespace-nowrap' },
  { key: 'sl_reason', label: 'SL Reason', truncate: true, className: 'max-w-[130px] text-muted-foreground' },
  { key: 'sl_action', label: 'SL Action', className: 'whitespace-nowrap' },
  {
    key: 'last_comment_date',
    label: 'Last Comment Date',
    className: 'text-muted-foreground whitespace-nowrap',
    render: (val) => (val ? formatSlashDate(val) : ''),
  },
  {
    key: 'latest_comment',
    label: 'Last Comment',
    truncate: true,
    className: 'max-w-[180px] text-muted-foreground',
  },
];

// Impact dots: two rows of three. Top row reflects one metric (blue, or red
// when flagged); bottom row a second metric (black). Unfilled dots are muted.
// No backing API field exists yet — values are static placeholders cycled by
// row index so the column previews the range of states. Wire `top`/`flagged`/
// `bottom` to real referral data once the field lands.
const IMPACT_DOT_SAMPLES = [
  { top: 0, flagged: false, bottom: 0 },
  { top: 1, flagged: false, bottom: 1 },
  { top: 1, flagged: true, bottom: 1 },
  { top: 3, flagged: false, bottom: 3 },
];

function ImpactDots({ top = 0, flagged = false, bottom = 0 }) {
  const topColor = flagged ? 'bg-destructive' : 'bg-primary';
  return (
    <div className="grid grid-cols-3 gap-1" aria-hidden="true">
      {Array.from({ length: 3 }, (_, i) => (
        <span
          key={`top-${i}`}
          className={cn('impact-dot', i < top ? topColor : 'bg-muted')}
        />
      ))}
      {Array.from({ length: 3 }, (_, i) => (
        <span
          key={`bottom-${i}`}
          className={cn('impact-dot', i < bottom ? 'bg-foreground' : 'bg-muted')}
        />
      ))}
    </div>
  );
}

function OverviewTable() {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Drawer state lives here since this component owns the row data. Both entry
  // points (row double-click, far-right expand icon) funnel through openDrawer.
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Medical Records drawer state — opened from the Medical Records column icon.
  const [medRecordsReferral, setMedRecordsReferral] = useState(null);
  const [medRecordsOpen, setMedRecordsOpen] = useState(false);
  // SL-disposition modal state. Editing the SL fields/last comment is gated to
  // SmartLight employees — non-SL users see plain read-only text.
  const [slModalOpen, setSlModalOpen] = useState(false);
  const [slModalRow, setSlModalRow] = useState(null);
  const [slModalField, setSlModalField] = useState(null);

  // Backend filter set derived from the shared CE filter selections (model
  // type today; HCC / workflow / override plug in here later).
  const apiFilters = useCarrierEngagementApiFilters();

  // Monotonic request id: each load captures its id, and only the latest load
  // is allowed to commit its result. This makes rapid sequential filter clicks
  // last-write-wins — an earlier, slower response can't clobber a newer one,
  // and no click is dropped just because a prior query is still in flight.
  const requestIdRef = useRef(0);

  const canEditSl = isSmartLightEmployee();

  const openDrawer = useCallback((row) => {
    setSelectedReferral(row);
    setDrawerOpen(true);
  }, []);

  const openMedRecords = useCallback((row) => {
    setMedRecordsReferral(row);
    setMedRecordsOpen(true);
  }, []);

  const openSlModal = useCallback((row, field) => {
    setSlModalRow(row);
    setSlModalField(field);
    setSlModalOpen(true);
  }, []);

  // Update a single row in place after an SL disposition is posted — mirrors
  // CP's patchReferral. No full table reload.
  const patchRow = useCallback((id, patch) => {
    if (!id || !patch) return;
    setRows((prev) => prev.map((r) => (r.referral_id === id ? { ...r, ...patch } : r)));
    // Keep the open drawer's referral in sync if it's the same row.
    setSelectedReferral((prev) => (prev && prev.referral_id === id ? { ...prev, ...patch } : prev));
  }, []);

  const loadFirstPage = useCallback(async () => {
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEngagementReferrals({ filters: apiFilters });
      if (requestId !== requestIdRef.current) return; // superseded — drop it
      setRows(data.results || []);
      setTotalRecords(data.total_records || 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err?.message || 'Failed to load referrals');
      setRows([]);
    } finally {
      // Only the latest request controls the spinner, so an outpaced response
      // resolving late can't flip loading off while a newer load is running.
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [apiFilters]);

  // Refetches whenever the filter selection changes (apiFilters is memoized in
  // the context, so this only fires on a real change, not every render).
  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  // +2 for the trailing Medical Records and expand-icon columns.
  const colSpan = OVERVIEW_COLUMNS.length + 2;

  return (
    <>
      <div className="overflow-auto flex-1 min-h-0" data-testid="overview-table-scroll">
        <Table className="min-w-[1800px]" wrapperClassName="h-full">
          <TableHeader>
            <TableRow className="bg-muted">
              {OVERVIEW_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  data-testid={`overview-col-${column.key}`}
                  className="h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle text-foreground"
                >
                  {column.label}
                </TableHead>
              ))}
              {/* Medical Records column — cell icons TBD. */}
              <TableHead
                data-testid="overview-col-medical_records"
                className="h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle text-foreground text-center"
              >
                Medical Records
              </TableHead>
              {/* Referral Drawer column — its cell icon opens the drawer. */}
              <TableHead
                data-testid="overview-col-referral_drawer"
                className="h-8 px-3 font-semibold text-xs whitespace-nowrap align-middle text-foreground text-center"
              >
                Referral Drawer
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && error && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6 text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            )}
            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-center py-6 text-muted-foreground">
                  No referrals found.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              !error &&
              rows.map((row, index) => (
                <TableRow
                  key={row.referral_id ?? index}
                  data-testid={`overview-row-${row.referral_id}`}
                  onDoubleClick={() => openDrawer(row)}
                  className="hover:bg-muted/30 select-none cursor-pointer"
                >
                  {OVERVIEW_COLUMNS.map((column) => {
                    const fieldName = column.displayKey || column.key;
                    const val = row[fieldName];
                    const content = column.render ? column.render(val, row) : (val ?? '');
                    const editable = canEditSl && SL_EDITABLE_COLUMNS.has(column.key);
                    return (
                      <TableCell
                        key={column.key}
                        data-testid={`cell-${column.key}`}
                        className={cn(
                          'px-3 py-1 text-foreground text-left align-middle',
                          column.textSize || 'text-xs',
                          column.className
                        )}
                      >
                        <div className="flex items-center min-w-0">
                          {editable ? (
                            <button
                              type="button"
                              data-testid={`sl-edit-${column.key}`}
                              onClick={(e) => {
                                // Don't let the click bubble to the row's
                                // onDoubleClick drawer-open handler.
                                e.stopPropagation();
                                openSlModal(row, column.key);
                              }}
                              className="flex items-center justify-between gap-1 w-full min-w-0 text-left hover:text-primary transition-colors"
                              title={column.truncate ? (val ?? '') : undefined}
                            >
                              <span className={cn('min-w-0', column.truncate && 'truncate')}>
                                {content}
                              </span>
                              {/* Affordance from the design: SL fields open the
                                  modal via a chevron; Last Comment via a "+". */}
                              {column.key === 'latest_comment' ? (
                                <Plus className="h-3 w-3 text-muted-foreground shrink-0" />
                              ) : (
                                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                              )}
                            </button>
                          ) : column.truncate ? (
                            <span className="truncate" title={val ?? ''}>
                              {content}
                            </span>
                          ) : (
                            content
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                  {/* Medical Records cell — records icon + placeholder impact dots.
                      Hovering highlights the control and shows a tooltip;
                      clicking opens the MedicalRecordsDrawer. */}
                  <TableCell
                    data-testid={`cell-medical_records-${row.referral_id}`}
                    className="px-3 py-1 align-middle"
                  >
                    <div className="flex justify-center">
                      <Tooltip
                        content="Open medical records"
                        position="bottom"
                        variant="light"
                      >
                        <button
                          type="button"
                          data-testid={`medical-records-${row.referral_id}`}
                          aria-label="Open medical records"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMedRecords(row);
                          }}
                          className="inline-flex items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-200 transition-colors"
                        >
                          <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" />
                          <ImpactDots {...IMPACT_DOT_SAMPLES[index % IMPACT_DOT_SAMPLES.length]} />
                        </button>
                      </Tooltip>
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-1 text-center align-middle">
                    <button
                      type="button"
                      data-testid={`overview-expand-${row.referral_id}`}
                      aria-label="Open referral drawer"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDrawer(row);
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                      <PanelRightClose className="h-4 w-4" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      {!loading && !error && rows.length > 0 && (
        <div className="sticky bottom-0 z-10 flex items-center justify-center gap-4 border-t border-border bg-accent px-3 py-1">
          <span className="text-xs text-muted-foreground">
            {rows.length} of {totalRecords.toLocaleString()} results
          </span>
          {/* TODO: wire up download */}
          <button
            type="button"
            className="h-6 px-4 text-xs font-semibold flex items-center gap-1.5 text-muted-foreground hover:text-foreground bg-background hover:bg-accent border border-muted-foreground/30 hover:border-foreground rounded transition-colors"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        </div>
      )}

      <EngagementReferralDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        referral={selectedReferral}
        canEditSl={canEditSl}
        onSlPosted={patchRow}
      />

      <MedicalRecordsDrawer
        open={medRecordsOpen}
        onClose={() => setMedRecordsOpen(false)}
        referral={medRecordsReferral}
      />

      {slModalOpen && (
        <SlDispositionModal
          open={slModalOpen}
          onClose={() => setSlModalOpen(false)}
          refId={slModalRow?.referral_id}
          currentResponse={slModalRow?.sl_response ?? null}
          initialFocusField={slModalField}
          needsReplacesFetch
          onSuccess={(patch) => patchRow(slModalRow?.referral_id, patch)}
        />
      )}
    </>
  );
}

export { OverviewTable, OVERVIEW_COLUMNS };
